import { collection, query, where, getDocsFromServer } from 'firebase/firestore'
import { db } from '@/firebase'
import { getScopedUsersSnapshot } from '@/composables/useUsers'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'
import { studentReadQueryFilters, REFERRAL_TEAM_ROLES } from '../referralAccessPlan.js'

// Role-aware read checks: the two core list queries are attempted and the
// server's answer is asserted against the rules mirror. A correct DENIAL for a
// non-full-read role counts as a pass (mirrors permissionProbes).
export default {
  id: 'readAccess',
  label: 'Students & users read access',
  description:
    'Student and user list queries succeed or are denied as the rules require for your role.',
  destructive: false,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const expectStudents = RULE_EXPECTATIONS.studentListReadable(role)
    const expectUsers = RULE_EXPECTATIONS.usersListReadable(role)

    await step(`school-only student query (expected: ${fmtExpected(expectStudents)})`, async () => {
      let count = null
      const { allowed } = await attempt(async () => {
        const base = collection(db, 'students')
        const q = ctx.schoolId ? query(base, where('schoolId', '==', ctx.schoolId)) : base
        const snap = await getDocsFromServer(q)
        count = snap.size
      })
      const detail = accessDetail(expectStudents, allowed)
      return allowed ? `${detail} — ${count} students` : detail
    })

    if (!REFERRAL_TEAM_ROLES.includes(role)) {
      const filters = studentReadQueryFilters(role, ctx.user.uid)
      if (filters.length) {
        await step('access-filtered student queries (expected: allowed)', async () => {
          const ids = new Set()
          for (const parts of filters) {
            const constraints = parts.map(([field, op, value]) => where(field, op, value))
            if (ctx.schoolId) constraints.push(where('schoolId', '==', ctx.schoolId))
            const snap = await getDocsFromServer(query(collection(db, 'students'), ...constraints))
            snap.docs.forEach((d) => ids.add(d.id))
          }
          return `allowed — ${ids.size} students across ${filters.length} authorized queries`
        })
      }
    }

    await step(`list users (expected: ${fmtExpected(expectUsers)})`, async () => {
      let count = null
      const { allowed } = await attempt(async () => {
        const snap = await getScopedUsersSnapshot()
        count = snap.size
      })
      const detail = accessDetail(expectUsers, allowed)
      return allowed ? `${detail} — ${count} users` : detail
    })
  }
}
